import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Timer, Pause, Play, Square, Phone, PhoneCall, Navigation } from 'lucide-react';

const MODES = [
  { key: 'hold', label: 'On Hold', color: 'amber', icon: Pause },
  { key: 'call', label: 'On Call', icon: PhoneCall, color: 'green' },
  { key: 'ivr', label: 'IVR Nav', icon: Navigation, color: 'blue' },
];

export default function LiveCallTimer({ onTimerUpdate, compact = false }) {
  const [running, setRunning] = useState(false);
  const [mode, setMode] = useState('hold');
  const [elapsed, setElapsed] = useState(0);
  const [holdTime, setHoldTime] = useState(0);
  const [callTime, setCallTime] = useState(0);
  const intervalRef = useRef(null);
  const modeRef = useRef(mode);

  useEffect(() => { modeRef.current = mode; }, [mode]);

  useEffect(() => {
    if (running) {
      intervalRef.current = setInterval(() => {
        setElapsed(e => e + 1);
        if (modeRef.current === 'hold' || modeRef.current === 'ivr') {
          setHoldTime(h => h + 1);
        } else {
          setCallTime(c => c + 1);
        }
      }, 1000);
    } else if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, [running]);

  useEffect(() => {
    if (onTimerUpdate) {
      onTimerUpdate({ elapsed, holdTime, callTime, running, mode });
    }
  }, [elapsed, holdTime, callTime, running, mode, onTimerUpdate]);

  const fmt = (s) => `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;
  const currentMode = MODES.find(m => m.key === mode);
  const ModeIcon = currentMode?.icon || Timer;

  const start = () => setRunning(true);
  const pause = () => setRunning(false);
  const stop = useCallback(() => {
    setRunning(false);
    if (onTimerUpdate) {
      onTimerUpdate({ elapsed, holdTime, callTime, running: false, mode, stopped: true });
    }
  }, [elapsed, holdTime, callTime, mode, onTimerUpdate]);

  const reset = () => {
    setRunning(false);
    setElapsed(0);
    setHoldTime(0);
    setCallTime(0);
    setMode('hold');
  };

  const colorMap = {
    amber: { bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-700', badge: 'bg-amber-100 text-amber-800', dot: 'bg-amber-500' },
    green: { bg: 'bg-emerald-50', border: 'border-emerald-200', text: 'text-emerald-700', badge: 'bg-emerald-100 text-emerald-800', dot: 'bg-emerald-500' },
    blue: { bg: 'bg-blue-50', border: 'border-blue-200', text: 'text-blue-700', badge: 'bg-blue-100 text-blue-800', dot: 'bg-blue-500' },
  };
  const colors = colorMap[currentMode?.color || 'amber'];

  if (compact) {
    return (
      <div className="flex items-center gap-2">
        {running && (
          <span className={`flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-semibold ${colors.badge}`}>
            <span className={`w-2 h-2 rounded-full ${colors.dot} animate-pulse`} />
            {currentMode.label} {fmt(elapsed)}
          </span>
        )}
        {!running && elapsed > 0 && (
          <span className="text-xs text-slate-500 font-mono">{fmt(elapsed)}</span>
        )}
        {!running && elapsed === 0 && (
          <button onClick={start} className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors">
            <Timer size={14} /> Start Timer
          </button>
        )}
      </div>
    );
  }

  return (
    <div className={`rounded-xl border ${running ? colors.border : 'border-slate-200'} ${running ? colors.bg : 'bg-white'} p-4 transition-all`}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Timer size={18} className={running ? colors.text : 'text-slate-500'} />
          <span className="text-sm font-semibold text-slate-700">Call Timer</span>
        </div>
        <span className={`font-mono text-2xl font-bold tracking-wider ${running ? colors.text : 'text-slate-400'}`}>
          {fmt(elapsed)}
        </span>
      </div>

      {/* Mode selector */}
      <div className="flex gap-1.5 mb-3">
        {MODES.map(m => {
          const Icon = m.icon;
          const active = mode === m.key;
          const mColors = colorMap[m.color];
          return (
            <button
              key={m.key}
              onClick={() => setMode(m.key)}
              className={`flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-medium transition-all ${
                active ? `${mColors.badge} ring-1 ring-${m.color}-300` : 'text-slate-500 hover:bg-slate-100'
              }`}
            >
              <Icon size={13} /> {m.label}
            </button>
          );
        })}
      </div>

      {/* Controls */}
      <div className="flex items-center gap-2">
        {!running && elapsed === 0 && (
          <button onClick={start} className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium rounded-lg transition-colors">
            <Play size={14} /> Start
          </button>
        )}
        {running && (
          <button onClick={pause} className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 bg-slate-600 hover:bg-slate-700 text-white text-sm font-medium rounded-lg transition-colors">
            <Pause size={14} /> Pause
          </button>
        )}
        {!running && elapsed > 0 && (
          <button onClick={start} className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium rounded-lg transition-colors">
            <Play size={14} /> Resume
          </button>
        )}
        {elapsed > 0 && (
          <button onClick={stop} className="flex items-center justify-center gap-1 px-3 py-2 bg-red-100 hover:bg-red-200 text-red-700 text-sm font-medium rounded-lg transition-colors">
            <Square size={14} /> Stop
          </button>
        )}
        {elapsed > 0 && !running && (
          <button onClick={reset} className="px-3 py-2 text-slate-500 hover:text-slate-700 text-sm transition-colors">Reset</button>
        )}
      </div>

      {/* Time breakdown */}
      {elapsed > 0 && (
        <div className="flex gap-4 mt-3 pt-3 border-t border-slate-100 text-xs text-slate-500">
          <span>Hold: <span className="font-mono font-medium text-amber-600">{fmt(holdTime)}</span></span>
          <span>Talk: <span className="font-mono font-medium text-emerald-600">{fmt(callTime)}</span></span>
          <span>Total: <span className="font-mono font-medium text-slate-700">{fmt(elapsed)}</span></span>
        </div>
      )}
    </div>
  );
}
